import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllEPG, getChannels, getCategories } from '../services/api';
import { toast } from 'sonner';
import { Tv, Calendar, Clock, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

export default function EPGPage() {
  const [channels, setChannels] = useState([]);
  const [epgData, setEpgData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [channelsData, epg] = await Promise.all([
          getChannels({ limit: 50 }),
          getAllEPG()
        ]);
        setChannels(channelsData);
        setEpgData(epg);
      } catch (error) {
        console.error('Error fetching EPG:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleWatch = (channelId) => {
    navigate(`/watch/${channelId}`);
  };

  // Generate time slots
  const timeSlots = [];
  for (let i = 0; i < 24; i++) {
    timeSlots.push(`${i.toString().padStart(2, '0')}:00`);
  }

  const currentHour = new Date().getHours();

  if (loading) {
    return (
      <div className="space-y-6" data-testid="epg-loading">
        <h1 className="text-3xl font-bold">TV Guide</h1>
        <div className="h-[600px] rounded-xl skeleton" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="epg-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">TV Guide</h1>
          <p className="text-white/60 mt-1">Program schedule for {selectedDate.toLocaleDateString()}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={() => setSelectedDate(d => new Date(d.setDate(d.getDate() - 1)))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" className="rounded-full">
            <Calendar className="w-4 h-4 mr-2" />
            Today
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={() => setSelectedDate(d => new Date(d.setDate(d.getDate() + 1)))}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {channels.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-[rgb(31,31,34)] flex items-center justify-center mb-6">
            <Tv className="w-10 h-10 text-white/30" />
          </div>
          <h3 className="text-xl font-semibold mb-2">No Channels Available</h3>
          <p className="text-white/50 max-w-md">Add channels through the admin panel to see the TV guide</p>
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          {/* Time Header */}
          <div className="flex border-b border-white/5">
            <div className="w-48 flex-shrink-0 p-4 bg-[rgb(15,15,16)] border-r border-white/5">
              <span className="font-semibold">Channel</span>
            </div>
            <ScrollArea className="flex-1">
              <div className="flex">
                {timeSlots.map((time, index) => (
                  <div
                    key={time}
                    className={`w-32 flex-shrink-0 p-4 text-center border-r border-white/5 ${
                      index === currentHour ? 'bg-[rgb(124,58,237)]/20' : ''
                    }`}
                  >
                    <span className={`text-sm ${index === currentHour ? 'text-[rgb(124,58,237)] font-semibold' : 'text-white/60'}`}>
                      {time}
                    </span>
                  </div>
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>

          {/* Channel Rows */}
          <div className="max-h-[600px] overflow-y-auto">
            {channels.slice(0, 20).map((channel) => {
              const channelPrograms = epgData.filter(p => p.channel_id === channel.epg_id);
              
              return (
                <div key={channel.id} className="flex border-b border-white/5 hover:bg-white/5 transition-colors">
                  {/* Channel Info */}
                  <div 
                    className="w-48 flex-shrink-0 p-4 bg-[rgb(10,10,10)] border-r border-white/5 flex items-center gap-3 cursor-pointer"
                    onClick={() => handleWatch(channel.id)}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[rgb(31,31,34)] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {channel.logo ? (
                        <img src={channel.logo} alt={channel.name} className="w-full h-full object-cover" />
                      ) : (
                        <Tv className="w-5 h-5 text-white/30" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{channel.name}</p>
                      <p className="text-xs text-white/40 truncate">{channel.group}</p>
                    </div>
                  </div>

                  {/* Program Timeline */}
                  <ScrollArea className="flex-1">
                    <div className="flex h-20 relative">
                      {timeSlots.map((time, index) => (
                        <div
                          key={time}
                          className={`w-32 flex-shrink-0 border-r border-white/5 ${
                            index === currentHour ? 'bg-[rgb(124,58,237)]/10' : ''
                          }`}
                        />
                      ))}
                      
                      {/* Programs overlay */}
                      {channelPrograms.length > 0 ? (
                        channelPrograms.slice(0, 5).map((program, idx) => (
                          <div
                            key={idx}
                            className="absolute top-2 bottom-2 bg-[rgb(124,58,237)]/20 border border-[rgb(124,58,237)]/30 rounded-lg p-2 overflow-hidden"
                            style={{
                              left: `${idx * 150 + 10}px`,
                              width: '140px'
                            }}
                          >
                            <p className="text-xs font-medium truncate">{program.title}</p>
                            <p className="text-xs text-white/40 truncate">{program.category}</p>
                          </div>
                        ))
                      ) : (
                        <div className="absolute inset-y-2 left-2 right-2 flex items-center">
                          <span className="text-xs text-white/30">No program data</span>
                        </div>
                      )}
                    </div>
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
